import React, { useState } from 'react';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import { reading } from '../domain/reading';
import ReportTable from '../components/reports/ReportTable';
import ReportResultDrawer from '../components/reports/ReportResultDrawer';
import CreateReportDrawer from '../components/reports/CreateReportDrawer';
import {
  getReportType, defaultParams, reportFormatParam, reportStudentChoices, reportStudentQuickSelects, REPORTS_WORDING as W,
} from '../domain/reports';
import { teacherDisplayName } from '../domain/people';
import { revenueExpenseCategories, revenueExpensePayees } from '../domain/expenses';
import { todayISO } from '../domain/clock';
import { coreSync } from '../lib/core';
import { useReports } from '../data/useReports';
import { useExpenses } from '../data/useExpenses';
import { useFamilies } from '../data/useFamilies';
import { useSettings } from '../data/useSettings';
import { useStudents } from '../data/useStudents';
import { useTags } from '../data/useTags';
import { useCategories } from '../data/useCategories';
import { useAuth } from '../context/AuthContext';
import useDocumentLink from '../hooks/useDocumentLink';
import s from '../components/reports/reports.module.css';

// Business Reports (SPEC.md §4).
//
// Assembly. The parameter form is generated from the catalogue in
// domain/reports.js rather than hand-built thirteen times, so adding a report
// type is a registry entry.

// PDF is a format every report can be had in (#621): the server renders it
// from the stored rows (`documentLink`), so nothing in this build has to name
// a service for it.
const PDF_AVAILABLE = true;

export default function ReportsPage() {
  const {
    reports, createReport, waitingFor, loading, failed,
  } = useReports();
  // Download PDF (#621): the server's PDF of a report, by link.
  const documentLink = useDocumentLink();
  // Business Settings → Accounts & Invoices → Default Balance Date. Family
  // Balances' "As of" opens on it instead of on today, which is the whole point
  // of the setting: a studio that strikes its balances on the last of the month
  // should not retype the date every time.
  const { settings, nameFormats } = useSettings();
  // The Students control (#795): the roster it lists, its Quick Select's
  // tags and the teacher it names; and the lesson categories Attendance's
  // and Lessons Provided's Categories list.
  const { students } = useStudents();
  const { tags } = useTags();
  const { categories } = useCategories();
  const { user } = useAuth();
  // Revenue & Expenses' Categories and Payees pickers (#601) — the options
  // each lists, not the filtering itself, which is the core's.
  const { entries: expenseEntries, customCategories, categoriesSeeded } = useExpenses();
  const { families } = useFamilies();

  const [open, setOpen] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [params, setParams] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);

  // The catalogue entry, with its Report Format parameter the core's — PDF
  // selectable, and the default (#485, #621).
  const rawType = typeId ? getReportType(typeId) : null;
  const type = rawType && {
    ...rawType,
    params: rawType.params.map((p) => {
      if (p.id === 'format') return reportFormatParam(PDF_AVAILABLE);
      if (p.type === 'categories') return { ...p, options: revenueExpenseCategories(customCategories, categoriesSeeded) };
      if (p.type === 'eventCategories') return { ...p, options: categories.map((c) => c.label) };
      if (p.type === 'students') {
        return {
          ...p,
          choices: (includeInactive) => reportStudentChoices(students, nameFormats?.student, includeInactive),
          quickSelects: reportStudentQuickSelects(
            students, tags, teacherDisplayName(settings, user?.displayName) || settings.name || null,
          ),
        };
      }
      if (p.type === 'payees') {
        return { ...p, options: revenueExpensePayees(expenseEntries, families.map((f) => f.lastName)) };
      }
      return p;
    }),
  };

  // Create waits, disabled with the core's reason, until every list the
  // report reads has answered (#646). Disabled rather than deferred: a run
  // queued behind a read is a write the teacher can no longer see or cancel,
  // made from a form they may have left, and behind a read that failed
  // (#647) it would never be made at all. A disabled button is in view, says
  // why, and enables itself as the reads land — usually before it is reached.
  const waiting = typeId ? waitingFor(typeId, params).reason : null;

  const pickType = (id) => {
    setTypeId(id);
    setParams(defaultParams(id, {
      balanceDate: coreSync().resolveBalanceDate(settings.invoicing, todayISO()),
      pdfAvailable: PDF_AVAILABLE,
    }));
  };
  const close = () => { setOpen(false); setTypeId(''); setError(null); };

  const run = async () => {
    setSaving(true);
    setError(null);
    try {
      await createReport({ typeId, label: type.label, params });
      close();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const isEmpty = (!loading || failed) && reports.length === 0;

  return (
    <AppShell title={W.pageTitle}>
      <div className={s.page}>
        <div className={s.bar}>
          <Button onClick={() => setOpen(true)}>{W.createButton}</Button>
        </div>

        {isEmpty ? (
          <div className={s.wrap}>
            <EmptyState
              reading={reading(!loading, reports.length, failed)}
              title={W.emptyTitle}
              description={W.emptyDescription}
              actionLabel={W.createButton}
              onAction={() => setOpen(true)}
            />
          </div>
        ) : (
          <ReportTable reports={reports} onOpen={setViewing} pdfAvailable={PDF_AVAILABLE} />
        )}
      </div>

      <ReportResultDrawer
        report={reports.find((r) => r.id === viewing) || null}
        onClose={() => setViewing(null)}
        documentLink={documentLink}
      />

      <CreateReportDrawer
        open={open}
        onClose={close}
        typeId={typeId}
        type={type}
        params={params}
        error={error}
        saving={saving}
        waiting={waiting}
        onPickType={pickType}
        onParamChange={(id, v) => setParams((prev) => ({ ...prev, [id]: v }))}
        onRun={run}
      />
    </AppShell>
  );
}
